import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Landmark, UserPlus } from "lucide-react";
import { useLang } from "@/lib/i18n";

export async function startCheckout(lookup_key, url, opts = {}) {
  const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin, url, upload_ref: opts.upload_ref, upload_kind: opts.upload_kind });
  window.location.href = data.checkout_url;
}

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const nav = useNavigate();
  const { t } = useLang();
  const { user: authUser } = useAuth();
  const [state, setState] = useState({ phase: "checking", attempts: 0 });
  const timer = useRef(null);

  useEffect(() => {
    if (!sessionId) { setState({ phase: "error" }); return; }
    // Si l'utilisateur n'est pas connecté : on utilise l'endpoint public, pas de polling,
    // puis on l'oriente vers l'inscription pré-remplie si un compte doit être créé.
    if (!authUser) {
      (async () => {
        try {
          const { data } = await api.get(`/payments/public/${sessionId}`);
          if (data.needs_registration) {
            const q = new URLSearchParams({ email: data.customer_email || "", session_id: sessionId, prepaid: "1" });
            setState({ phase: "needs_register", email: data.customer_email });
            setTimeout(() => nav(`/register?${q.toString()}`), 2000);
            return;
          }
          if (data.payment_status === "paid" && data.account_exists) {
            // Compte existant mais utilisateur non connecté : propose de se connecter.
            const q = new URLSearchParams({ email: data.customer_email || "", next: `/payment/success?session_id=${sessionId}` });
            setState({ phase: "needs_login", email: data.customer_email });
            setTimeout(() => nav(`/login?${q.toString()}`), 2000);
            return;
          }
          setState({ phase: "checking" });
        } catch (e) {
          setState({ phase: "error" });
        }
      })();
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          // Paiement encaissé : on ne bloque plus jamais l'utilisateur.
          // Si le fulfillment serveur a échoué, on l'informe sans écran d'erreur
          // et on le ramène sur son tableau de bord où l'équipe pourra rejouer.
          if (data.lookup_key === "scan_full") {
            if (data.scan_id) {
              setState({ phase: "done" });
              setTimeout(() => nav(`/app/scan/${data.scan_id}`), 1200);
              return;
            }
            if (data.fulfil_error) {
              setState({ phase: "paid_pending", detail: data.fulfil_error });
              setTimeout(() => nav("/app"), 2500);
              return;
            }
            // Fulfillment encore en cours (tâche async) — on continue à poller
            setState({ phase: "processing" });
          } else {
            setState({ phase: "done" });
            setTimeout(() => nav("/app/billing"), 1200);
            return;
          }
        } else if (data.payment_status === "processing") {
          // SEPA : mandat signé, débit en cours (3-5 jours ouvrés)
          setState({ phase: "sepa_pending" });
          return;
        } else if (["expired", "failed"].includes(data.payment_status)) {
          setState({ phase: "error" }); return;
        }
      } catch (e) { /* transient error — will retry within 90 attempts */
        console.debug("Payment status poll retry:", e?.message);
      }
      if (attempts < 90) timer.current = setTimeout(poll, 2000);
      else {
        // Timeout : si on a vu "paid" au moins une fois on redirige quand même
        setState({ phase: "paid_pending" });
        setTimeout(() => nav("/app"), 2500);
      }
    };
    poll();
    return () => clearTimeout(timer.current);
  }, [sessionId, nav, authUser]);

  const content = {
    checking: { Icon: Loader2, spin: true, title: t("pay.checking"), sub: t("pay.checking.sub") },
    processing: { Icon: Loader2, spin: true, title: t("pay.processing"), sub: t("pay.processing.sub") },
    paid_pending: {
      Icon: CheckCircle2, ok: true,
      title: "Paiement validé",
      sub: "Nous finalisons votre scan. Vous êtes redirigé vers votre tableau de bord — l'équipe EmpCo prend le relais si nécessaire et vous recevrez un e-mail dès que votre scan est prêt.",
    },
    needs_register: {
      Icon: UserPlus, ok: true,
      title: "Paiement validé — créez votre compte",
      sub: `Nous avons bien reçu votre règlement${state.email ? ` (${state.email})` : ""}. Pour accéder à votre scan prépayé, finalisez la création de votre compte EmpCo en quelques secondes.`,
    },
    needs_login: {
      Icon: UserPlus, ok: true,
      title: "Paiement validé — connectez-vous",
      sub: `Un compte EmpCo existe déjà pour ${state.email || "cette adresse"}. Connectez-vous pour récupérer votre scan prépayé.`,
    },
    sepa_pending: { Icon: Landmark, title: t("pay.sepa_pending"), sub: t("pay.sepa_pending.sub"), ok: true },
    done: { Icon: CheckCircle2, title: t("pay.done"), sub: t("pay.done.sub"), ok: true },
    error: { Icon: XCircle, title: t("pay.error"), sub: state.detail || t("pay.error.sub"), err: true },
    timeout: { Icon: XCircle, title: t("pay.timeout"), sub: t("pay.timeout.sub"), err: true },
  }[state.phase];

  return (
    <div className="App min-h-screen brand-mesh">
      <Header variant="public" />
      <main className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
        <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6 ${content.ok ? "bg-secondary/15 text-secondary" : content.err ? "bg-red-100 text-red-600" : "bg-accent text-primary"}`}>
          <content.Icon className={`w-8 h-8 ${content.spin ? "animate-spin" : ""}`} />
        </div>
        <h1 className="text-3xl md:text-4xl mb-3" data-testid="payment-status-title">{content.title}</h1>
        <p className="text-muted-foreground leading-relaxed">{content.sub}</p>
        {sessionId && <p className="font-mono text-[11px] text-muted-foreground mt-6 break-all">session {sessionId}</p>}
        <Link to="/app" className="btn-primary mt-10 inline-flex" data-testid="payment-go-dashboard">{t("nav.dashboard")} <ArrowRight className="w-4 h-4" /></Link>
      </main>
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useLang();
  return (
    <div className="App min-h-screen brand-mesh">
      <Header variant="public" />
      <main className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="payment-cancel-page">
        <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6 bg-amber-100 text-amber-600"><XCircle className="w-8 h-8" /></div>
        <h1 className="text-3xl md:text-4xl mb-3">{t("pay.cancel")}</h1>
        <p className="text-muted-foreground">{t("pay.cancel.sub")}</p>
        <Link to="/app" className="btn-outline mt-10 inline-flex" data-testid="payment-cancel-back">{t("nav.dashboard")}</Link>
      </main>
    </div>
  );
}
